/**
 * Redux Store Configuration
 * Enhanced with industry best practices:
 * - Comprehensive middleware stack
 * - Performance monitoring
 * - Dev tools enhancement
 * - Type-safe hooks and selectors
 * - Extensible architecture for feature modules
 */

import {
  configureStore,
  Middleware,
  isRejectedWithValue,
  AnyAction,
} from "@reduxjs/toolkit";
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
  useStore,
} from "react-redux";
import { apiSlice } from "../features/api/apiSlice";
import authReducer from "./slices/authSlice";
import dateFilterReducer from "./slices/dateFilterSlice";
import messagesReducer from "./slices/messagesSlice";
import paymentsReducer from "./slices/paymentsSlice";
import analyticsReducer from "./slices/analyticsSlice";
import offersReducer from "./slices/offersSlice";
import mallsReducer from "./slices/mallsSlice";
import storesReducer from "./slices/storesSlice";

/**
 * Environment detection
 */
const isDevelopment = process.env.NODE_ENV === "development";

/**
 * Custom middleware for logging actions in development
 * Logs dispatched actions with timestamp and duration
 */
const actionLoggerMiddleware: Middleware =
  () => (next) => (action: unknown) => {
    if (
      isDevelopment &&
      typeof action === "object" &&
      action &&
      "type" in action
    ) {
      const typedAction = action as AnyAction;
      if (!typedAction.type.includes("api/")) {
        const timestamp = new Date().toISOString();
        console.groupCollapsed(
          `%c Action: ${typedAction.type}`,
          "color: #4CAF50; font-weight: bold",
          timestamp
        );
        console.log(
          "%c Payload:",
          "color: #2196F3; font-weight: bold",
          (typedAction as any).payload
        );
        console.groupEnd();
      }
    }
    return next(action);
  };

/**
 * Custom middleware for handling RTK Query errors
 * Provides centralized error handling and logging
 */
const rtkQueryErrorLogger: Middleware = () => (next) => (action: unknown) => {
  if (isRejectedWithValue(action)) {
    const error = action.payload as any;
    const meta = (action as any).meta;
    console.error("RTK Query Error:", {
      endpoint: meta?.arg?.endpointName || "Unknown",
      status: error?.status,
      message: error?.data?.message || error?.error,
      timestamp: new Date().toISOString(),
    });

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error);
  }
  return next(action);
};

/**
 * Performance monitoring middleware
 * Tracks action execution time in development
 */
const performanceMonitorMiddleware: Middleware =
  () => (next) => (action: unknown) => {
    if (isDevelopment) {
      const start = performance.now();
      const result = next(action);
      const end = performance.now();
      const duration = end - start;

      // Warn if action takes longer than 16ms (one frame at 60fps)
      if (
        duration > 16 &&
        typeof action === "object" &&
        action &&
        "type" in action
      ) {
        const typedAction = action as AnyAction;
        if (!typedAction.type.includes("api/")) {
          console.warn(
            `%c Slow Action: ${typedAction.type}`,
            "color: #FF9800; font-weight: bold",
            `${duration.toFixed(2)}ms`
          );
        }
      }
      return result;
    }
    return next(action);
  };

/**
 * Root reducer combining all feature reducers
 * Organized by domain for scalability
 */
const rootReducer = {
  // Domain: Authentication & Authorization
  auth: authReducer,

  // Domain: UI State & Filters
  dateFilter: dateFilterReducer,

  // Domain: Feature-specific UI State
  messages: messagesReducer,
  payments: paymentsReducer,
  analytics: analyticsReducer,
  offers: offersReducer,
  malls: mallsReducer,
  stores: storesReducer,

  // Domain: API & Data Fetching
  [apiSlice.reducerPath]: apiSlice.reducer,
};

/**
 * Middleware configuration
 * Ordered for optimal performance and debugging
 */
const getMiddlewareConfig = (getDefaultMiddleware: any) => {
  const middleware = getDefaultMiddleware({
    // Serializable check configuration for Date objects
    serializableCheck: {
      // Ignore date-related actions
      ignoredActions: [
        "dateFilter/setSingleDate",
        "dateFilter/setDateRange",
        "dateFilter/setToday",
        "dateFilter/setYesterday",
        "dateFilter/setLast7Days",
        // Ignore RTK Query internal actions
        "api/executeMutation/fulfilled",
        "api/executeQuery/fulfilled",
      ],
      // Ignore date fields in state
      ignoredPaths: [
        "dateFilter.selectedDate",
        "dateFilter.dateRange",
        "dateFilter.dateRange.start",
        "dateFilter.dateRange.end",
        "analytics.dailyCache.timestamp",
        "analytics.monthlyCache.timestamp",
        "analytics.yearlyCache.timestamp",
      ],
      // Ignore date fields in action payloads
      ignoredActionPaths: [
        "payload.selectedDate",
        "payload.dateRange",
        "meta.arg",
      ],
    },
    // Immutability check - disable in production for performance
    immutableCheck: isDevelopment,
    // Thunk check
    thunk: true,
  });

  // Add RTK Query middleware (required)
  middleware.push(apiSlice.middleware);

  // Add custom middleware in development
  if (isDevelopment) {
    middleware.push(performanceMonitorMiddleware);
    middleware.push(actionLoggerMiddleware);
  }

  // Add error logger (both dev and prod)
  middleware.push(rtkQueryErrorLogger);

  return middleware;
};

/**
 * Redux DevTools configuration
 * Enhanced with better serialization and limits
 */
const devToolsConfig: any = {
  // Max number of actions stored in DevTools
  maxAge: 50,
  // Trace action origin
  trace: isDevelopment,
  // Limit trace to 10 entries
  traceLimit: 10,
  // Features to enable
  features: {
    pause: true,
    lock: true,
    persist: true,
    export: true,
    import: "custom",
    jump: true,
    skip: true,
    reorder: true,
    dispatch: true,
    test: true,
  },
  // Action sanitizer for large payloads
  actionSanitizer: (action: any) => {
    // Truncate large API responses in DevTools
    if (action.type.includes("api/") && action.payload?.data) {
      return {
        ...action,
        payload: {
          ...action.payload,
          data: Array.isArray(action.payload.data)
            ? `[Array(${action.payload.data.length})...]`
            : "[Object]...",
        },
      };
    }
    return action;
  },
  // State sanitizer for large cache
  stateSanitizer: (state: any) => {
    // Truncate analytics cache in DevTools
    if (state.analytics) {
      return {
        ...state,
        analytics: {
          ...state.analytics,
          dailyCache: state.analytics.dailyCache.data
            ? { ...state.analytics.dailyCache, data: "[Cached Data]" }
            : state.analytics.dailyCache,
          monthlyCache: state.analytics.monthlyCache.data
            ? { ...state.analytics.monthlyCache, data: "[Cached Data]" }
            : state.analytics.monthlyCache,
          yearlyCache: state.analytics.yearlyCache.data
            ? { ...state.analytics.yearlyCache, data: "[Cached Data]" }
            : state.analytics.yearlyCache,
        },
      };
    }
    return state;
  },
};

/**
 * Configure Redux Store
 * Production-ready with performance optimizations
 */
export const store = configureStore({
  reducer: rootReducer,
  middleware: getMiddlewareConfig,
  devTools: isDevelopment ? devToolsConfig : false,
});

/**
 * Infer types from store
 * Enables full TypeScript support throughout the app
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;

/**
 * Pre-typed hooks for convenience and type safety
 * Use these instead of plain `useDispatch` and `useSelector`
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore = () => useStore<AppStore>();

/**
 * Helper for creating typed selectors
 * Example: const selectUser = createAppSelector((state) => state.auth.userDetails)
 */
export const createAppSelector = <T>(
  selector: (state: RootState) => T
): ((state: RootState) => T) => selector;

/**
 * Helper for batch dispatching multiple actions
 * Improves performance by reducing re-renders
 */
export const batchActions = (actions: Array<() => void>) => {
  // RTK automatically batches in React 18+
  actions.forEach((action) => action());
};

/**
 * Store subscription helper for listening to specific state changes
 * Example: subscribeToStore((state) => state.auth.isAuthenticated, callback)
 */
export const subscribeToStore = <T>(
  selector: (state: RootState) => T,
  callback: (value: T, prevValue: T) => void
) => {
  let prevValue = selector(store.getState());

  return store.subscribe(() => {
    const currentValue = selector(store.getState());
    if (currentValue !== prevValue) {
      callback(currentValue, prevValue);
      prevValue = currentValue;
    }
  });
};

/**
 * Performance monitoring - Log store state size in development
 */
if (isDevelopment) {
  // Log initial state size
  const stateSize = JSON.stringify(store.getState()).length;
  console.log(
    `%c Redux Store Initialized`,
    "color: #9C27B0; font-weight: bold; font-size: 14px",
    `\nState Size: ${(stateSize / 1024).toFixed(2)} KB`
  );

  // Monitor state size changes
  let lastSize = stateSize;
  store.subscribe(() => {
    const currentSize = JSON.stringify(store.getState()).length;
    const diff = currentSize - lastSize;
    if (Math.abs(diff) > 10240) {
      // 10KB threshold
      console.warn(
        `%c State Size Changed:`,
        "color: #FF5722; font-weight: bold",
        `${diff > 0 ? "+" : ""}${(diff / 1024).toFixed(2)} KB (Total: ${(
          currentSize / 1024
        ).toFixed(2)} KB)`
      );
    }
    lastSize = currentSize;
  });
}

/**
 * Export store instance for use in non-React code
 * Use sparingly - prefer hooks in components
 */
export default store;
